import { MouseEvent, ReactElement, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link, useHistory } from 'react-router-dom';
import formatUserName from 'src/utils/formatUserName';

import { CalendarPreferencesDialog } from '@components/CalendarPreferencesDialog/CalendarPreferencesDialog';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LogoutIcon from '@mui/icons-material/Logout';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import {
  Avatar,
  Badge,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';

import { BADGE_MAX } from '../notifications/notifications.utils.js';

export type UserProfileDropdownNotifications = {
  unread: number;
  onOpen: (anchorEl: HTMLElement) => void;
};

export type UserProfileDropdownProps = {
  user?: { firstName?: string; lastName?: string } | null;
  loading?: boolean;
  notifications?: UserProfileDropdownNotifications;
  tourAnchorId?: string;
};

export function UserProfileDropdown({
  user,
  loading,
  notifications,
  tourAnchorId,
}: UserProfileDropdownProps): ReactElement {
  const intl = useIntl();
  const history = useHistory();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isPreferencesOpen, setPreferencesOpen] = useState(false);

  const handleOpen = (event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleOpenPreferences = () => {
    setPreferencesOpen(true);
    handleClose();
  };
  const handleClosePreferences = () => setPreferencesOpen(false);

  const handleOpenNotifications = () => {
    handleClose();
    if (buttonRef.current) notifications?.onOpen(buttonRef.current);
  };

  // The /logout route (Logout.tsx) is the single owner of the sign-out lifecycle:
  // it settles the server logout — including its transparent 401 refresh-and-retry —
  // BEFORE clearing local auth. Firing logout inline here and resetting auth in the
  // same tick raced that retry against an emptied store, so an expired access token
  // at sign-out time left every server-side refresh pair alive (PR #798 review).
  const handleLogout = () => {
    handleClose();
    history.push('/logout');
  };

  const initials = user?.firstName?.[0]?.toUpperCase() ?? '';
  const fullName = user ? formatUserName(user) : '';

  return (
    <>
      <Tooltip title="Account">
        <IconButton
          ref={buttonRef}
          data-testid="user-profile-button"
          id={tourAnchorId}
          size="small"
          sx={{ ml: 1 }}
          onClick={handleOpen}
        >
          <Badge
            color="error"
            data-testid="user-profile-unread-dot"
            invisible={!notifications?.unread}
            overlap="circular"
            variant="dot"
          >
            <Avatar
              sx={{
                width: 35,
                height: 35,
                bgcolor: 'secondary.main',
                fontSize: '0.875rem',
                fontWeight: 700,
              }}
            >
              {loading ? <CircularProgress color="inherit" size={16} /> : initials}
            </Avatar>
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        open={Boolean(anchorEl)}
        slotProps={{
          paper: { elevation: 4, sx: { minWidth: 220, mt: 0.5 } },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        onClose={handleClose}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography noWrap fontWeight={700} variant="subtitle2">
            {fullName}
          </Typography>
        </Box>
        <Divider />
        {notifications && (
          <MenuItem onClick={handleOpenNotifications}>
            <ListItemIcon>
              <NotificationsNoneOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {intl.formatMessage({ id: 'NOTIFICATIONS.TITLE', defaultMessage: 'Notifications' })}
            </ListItemText>
            {notifications.unread > 0 && (
              <Typography color="error" fontWeight={700} sx={{ ml: 2 }} variant="body2">
                {notifications.unread > BADGE_MAX ? `${BADGE_MAX}+` : notifications.unread}
              </Typography>
            )}
          </MenuItem>
        )}
        <MenuItem component={Link} to="/user-profile" onClick={handleClose}>
          <ListItemIcon>
            <AccountCircleIcon fontSize="small" />
          </ListItemIcon>
          My Profile
        </MenuItem>
        <MenuItem onClick={handleOpenPreferences}>
          <ListItemIcon>
            <CalendarMonthIcon fontSize="small" />
          </ListItemIcon>
          Calendar settings
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Sign Out
        </MenuItem>
      </Menu>

      {isPreferencesOpen && <CalendarPreferencesDialog onClose={handleClosePreferences} />}
    </>
  );
}
